/**
 * This is a copy of analytics-lib at version 0.0.93
 *
 * @plangrid-private cannot be accessed by some consumers of alloy,
 * and acc-issues is included in alloy, and analytics-lib is part of @plangrid-private.
 *
 * So we temporarily include the exports we need in our own library, so we can remove the dependency.
 *
 *
 */

import {
  Issue,
  IssueCustomAttribute,
  PlacementType,
  ProjectMemberType,
} from "@adsk/acc-issues-types";
import {
  AnalyticsEvent,
  EventParameters,
} from "@adsk/acc-issues-core/utils/analytics/analyticsTypes";
import { createAnalyticsEvent } from "@adsk/acc-issues-core/utils/analytics/createEvent";
import { IssueTemplateChange } from "../../IssuesTab/TemplatesConfig/IssueTemplateDetailPanel/IssueTemplateDetailPanelBody/typesForFields";
import { UpdateOrigin } from "./issueActionsTracking";
import { getAnalyticsPlacementType } from "@adsk/acc-issues-core/utils/analytics/getAnalyticsPlacementType";

type Uid = string;

export enum ProjectClassification {
  Template = "template",
  Project = "project",
}

export const IssueCreateClickAnalyticsEvent =
  createAnalyticsEvent("issue.create.click");

export const IssueCreateMarkupClickAnalyticsEvent = createAnalyticsEvent(
  "issue.create.markup.click"
);

export const IssueCreateMarkupTypeClickAnalyticsEvent = createAnalyticsEvent(
  "issue.create.markup.type.click"
);

export const IssueCreateMarkupTypeSearchAnalyticsEvent = createAnalyticsEvent(
  "issue.create.markup.type.search"
);

export const IssueCreateMarkupTypeClearSearchAnalyticsEvent =
  createAnalyticsEvent("issue.create.markup.type.clear.search");

export const IssueCreateCancelAnalyticsEvent = createAnalyticsEvent(
  "issue.create.cancel"
);

//currently not being used since it reqires some modification to the CreationModal
export class IssueCreateCancelConfirmationAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.create.cancel.confirmation";
  parameters: EventParameters;

  constructor(params: { issue_create_cancel_confirmation_action: string }) {
    this.parameters = {};
    this.parameters.issue_create_cancel_confirmation_action =
      params.issue_create_cancel_confirmation_action;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export const IssueDeleteClickAnalyticsEvent =
  createAnalyticsEvent("issue.delete.click");

export class IssueDeleteConfirmAnalyticsEvent implements AnalyticsEvent {
  name = "issue.delete.confirm";
  parameters: EventParameters;

  constructor(params: { issueUid: Uid; comment: boolean }) {
    this.parameters = {};
    this.parameters.issue_uid = params.issueUid;
    this.parameters.comment = params.comment;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueDeleteCancelAnalyticsEvent implements AnalyticsEvent {
  name = "issue.delete.cancel";
  parameters: EventParameters;

  constructor(params: { comment: boolean }) {
    this.parameters = {
      ...params,
    };
  }
}

export class IssueCreateEditFieldAnalyticsEvent implements AnalyticsEvent {
  name = "issue.create.edit.field";
  parameters: EventParameters;

  constructor(params: { field_type: any[] }) {
    this.parameters = {};
    this.parameters.field_type = params.field_type;
  }
}

export const IssueLogSettingClickAnalyticsEvent = createAnalyticsEvent(
  "issue.log.setting.click"
);

export class IssueLogSettingMenuClickAnalyticsEvent implements AnalyticsEvent {
  name = "issue.log.setting.menu.click";
  parameters: EventParameters;

  constructor(params: { issue_setting_dropdown_option_click: string }) {
    this.parameters = {};
    this.parameters.issue_setting_dropdown_option_click =
      params.issue_setting_dropdown_option_click;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingTabsClickAnalyticsEvent implements AnalyticsEvent {
  name = "issue.setting.tabs.click";
  parameters: EventParameters;

  constructor(params: { issue_setting_tabs_click: string }) {
    this.parameters = {};
    this.parameters.issue_setting_tabs_click = params.issue_setting_tabs_click;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingCreateClickAnalyticsEvent implements AnalyticsEvent {
  name = "issue.setting.create.click";
  parameters: EventParameters;

  constructor(params: {
    source: string;
    projectClassification: ProjectClassification;
  }) {
    this.parameters = {};
    this.parameters.source = params.source;
    this.parameters.project_classification = params.projectClassification;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingItemClickAnalyticsEvent implements AnalyticsEvent {
  name = "issue.setting.item.click";
  parameters: EventParameters;

  constructor(params: { issue_setting_item_click: string }) {
    this.parameters = {};
    this.parameters.issue_setting_item_click = params.issue_setting_item_click;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingCreateOptionsClickAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.create.options.click";
  parameters: EventParameters;

  constructor(params: { issue_setting_create_dropdown_option_click: string }) {
    this.parameters = {};
    this.parameters.issue_setting_create_dropdown_option_click =
      params.issue_setting_create_dropdown_option_click;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingPageViewAnalyticsEvent implements AnalyticsEvent {
  name = "issue.setting.page.view";
  parameters: EventParameters;

  constructor(params: { pageType: IssueConfigurationPageType }) {
    this.parameters = {};
    this.parameters.page_type = params.pageType;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingTypeCreateAnalyticsEvent implements AnalyticsEvent {
  name = "issue.setting.type.create";
  parameters: EventParameters;

  constructor(params: {
    typeUid: Uid;
    title: string;
    category: string;
    status: ActiveStatus;
    pinLabel: string;
  }) {
    this.parameters = {};
    this.parameters.type_uid = params.typeUid;
    this.parameters.title = params.title;
    this.parameters.category = params.category;
    this.parameters.status = params.status;
    this.parameters.pin_label = params.pinLabel;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingTypeCreateEditFieldAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.type.create.edit.field";
  parameters: EventParameters;

  constructor(params: { field_type: string[] }) {
    this.parameters = {};
    this.parameters.field_type = params.field_type;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingTypeEditAnalyticsEvent implements AnalyticsEvent {
  name = "issue.setting.type.edit";
  parameters: EventParameters;

  constructor(params: {
    typeUid: Uid;
    category: string;
    editedTitle?: string;
    editedStatus?: ActiveStatus;
    editedPinLabel?: string;
    editedCustomFields?: string[];
  }) {
    this.parameters = {};
    this.parameters.type_uid = params.typeUid;
    this.parameters.category = params.category;
    this.parameters.edited_title = params.editedTitle;
    this.parameters.edited_status = params.editedStatus;
    this.parameters.edited_pin_label = params.editedPinLabel;
    this.parameters.editedCustomFields = params.editedCustomFields;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingTypeDeleteClickAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.type.delete.click";
  parameters: EventParameters;

  constructor() {
    this.parameters = {};
    this.parameters["analyticsLib"] = "0.0.93";
  }
}
export class IssueSettingTypeDeleteAnalyticsEvent implements AnalyticsEvent {
  name = "issue.setting.type.delete";
  parameters: EventParameters;

  constructor(params: { typeUid: Uid }) {
    this.parameters = {};
    this.parameters.type_uid = params.typeUid;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingTypeCategoryCreateAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.type_category.create";
  parameters: EventParameters;

  constructor(params: {
    categoryUid: Uid;
    title: string;
    issueTypesCount: number;
    status: ActiveStatus;
  }) {
    this.parameters = {};
    this.parameters.category_uid = params.categoryUid;
    this.parameters.title = params.title;
    this.parameters.issue_types_count = params.issueTypesCount;
    this.parameters.status = params.status;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingTypeCategoryCreateEditFieldAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.type.category.create.edit.field";
  parameters: EventParameters;

  constructor(params: { field_type: string[] }) {
    this.parameters = {};
    this.parameters.field_type = params.field_type;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export const IssueSettingTypeCreateCancelAnalyticsEvent = createAnalyticsEvent(
  "issue.setting.type.create.cancel"
);

export class IssueSettingTypeCreateCancelConfirmationAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.type.create.cancel.confirmation";
  parameters: EventParameters;

  constructor(params: { issue_create_cancel_confirmation_action: string }) {
    this.parameters = {};
    this.parameters.issue_create_cancel_confirmation_action =
      params.issue_create_cancel_confirmation_action;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export const IssueSettingTypeCategoryCreateCancelAnalyticsEvent =
  createAnalyticsEvent("issue.setting.type.category.create.cancel");

export class IssueSettingTypeCategoryCreateCancelConfirmationAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.type.category.create.cancel.confirmation";
  parameters: EventParameters;

  constructor(params: { issue_create_cancel_confirmation_action: string }) {
    this.parameters = {};
    this.parameters.issue_create_cancel_confirmation_action =
      params.issue_create_cancel_confirmation_action;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingTypeCategoryEditAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.type_category.edit";
  parameters: EventParameters;

  constructor(params: {
    categoryUid: Uid;
    editedTitle?: string;
    editedStatus?: ActiveStatus;
  }) {
    this.parameters = {};
    this.parameters.category_uid = params.categoryUid;
    this.parameters.edited_title = params.editedTitle;
    this.parameters.edited_status = params.editedStatus;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}
export const IssueSettingTypeCategoryDeleteClickAnalyticsEvent =
  createAnalyticsEvent("issue.setting.type_category.delete.click");

export class IssueSettingTypeCategoryDeleteAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.type_category.delete";
  parameters: EventParameters;

  constructor(params: { categoryUid: Uid }) {
    this.parameters = {};
    this.parameters.category_uid = params.categoryUid;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingRootCauseCategoryCreateAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.root_cause_category.create";
  parameters: EventParameters;

  constructor(params: {
    rootCauseCategoryUid: Uid;
    title: string;
    rootCausesCount: number;
    status: ActiveStatus;
  }) {
    this.parameters = {};
    this.parameters.root_cause_category_uid = params.rootCauseCategoryUid;
    this.parameters.title = params.title;
    this.parameters.root_causes_count = params.rootCausesCount;
    this.parameters.status = params.status;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingRootCauseCategoryEditAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.root_cause_category.edit";
  parameters: EventParameters;

  constructor(params: {
    rootCauseCategoryUid: Uid;
    editedTitle?: string;
    editedStatus?: ActiveStatus;
  }) {
    this.parameters = {};
    this.parameters.root_cause_category_uid = params.rootCauseCategoryUid;
    this.parameters.edited_title = params.editedTitle;
    this.parameters.edited_status = params.editedStatus;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export const IssueSettingRootCauseCategoryDeleteClickAnalyticsEvent =
  createAnalyticsEvent("issue.setting.root_cause_category.delete.click");

export class IssueSettingRootCauseCategoryDeleteAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.root_cause_category.delete";
  parameters: EventParameters;

  constructor(params: { rootCauseCategoryUid: Uid }) {
    this.parameters = {};
    this.parameters.root_cause_category_uid = params.rootCauseCategoryUid;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingRootCauseCreateAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.root_cause.create";
  parameters: EventParameters;

  constructor(params: {
    rootCauseUid: Uid;
    rcCategoryName: string;
    title: string;
    status: ActiveStatus;
  }) {
    this.parameters = {};
    this.parameters.root_cause_uid = params.rootCauseUid;
    this.parameters.rc_category_name = params.rcCategoryName;
    this.parameters.title = params.title;
    this.parameters.status = params.status;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingRootCauseEditAnalyticsEvent implements AnalyticsEvent {
  name = "issue.setting.root_cause.edit";
  parameters: EventParameters;

  constructor(params: {
    rootCauseUid: Uid;
    rcCategoryName: string;
    editedTitle?: string;
    editedStatus?: ActiveStatus;
  }) {
    this.parameters = {};
    this.parameters.root_cause_uid = params.rootCauseUid;
    this.parameters.rc_category_name = params.rcCategoryName;
    this.parameters.edited_title = params.editedTitle;
    this.parameters.edited_status = params.editedStatus;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export const IssueSettingRootCauseDeleteClickAnalyticsEvent =
  createAnalyticsEvent("issue.setting.root_cause.delete.click");

export class IssueSettingRootCauseDeleteAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.root_cause.delete";
  parameters: EventParameters;

  constructor(params: { rootCauseUid: Uid }) {
    this.parameters = {};
    this.parameters.root_cause_uid = params.rootCauseUid;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingPermissionCreateAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.permission.create";
  parameters: EventParameters;

  constructor(params: {
    type: PermissionEntityTypes;
    permissionLevel: PermissionsLevels;
    projectClassification: ProjectClassification;
  }) {
    this.parameters = {};
    this.parameters.type = params.type;
    this.parameters.permission_level = params.permissionLevel;
    this.parameters.project_classification = params.projectClassification;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingPermissionEditAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.permission.edit";
  parameters: EventParameters;

  constructor(params: {
    type: PermissionEntityTypes;
    newPermissionLevel: PermissionsLevels;
    oldPermissionLevel: PermissionsLevels;
    projectClassification: ProjectClassification;
  }) {
    this.parameters = {};
    this.parameters.type = params.type;
    this.parameters.new_permission_level = params.newPermissionLevel;
    this.parameters.old_permission_level = params.oldPermissionLevel;
    this.parameters.project_classification = params.projectClassification;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingPermissionTypeEditAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.permission.type.edit";
  parameters: EventParameters;

  constructor(params: {
    allTypes: boolean;
    permittedTypesNum: number;
    permittedTypesPercent: number;
    entityType: PermissionEntityTypes;
  }) {
    this.parameters = {};
    this.parameters.all_types = params.allTypes;
    this.parameters.permitted_types_num = params.permittedTypesNum;
    this.parameters.permitted_types_percent = params.permittedTypesPercent;
    this.parameters.entity_type = params.entityType;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingPermissionDeleteAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.permission.delete";
  parameters: EventParameters;

  constructor(params: {
    type: PermissionEntityTypes;
    projectClassification: ProjectClassification;
  }) {
    this.parameters = {};
    this.parameters.type = params.type;
    this.parameters.project_classification = params.projectClassification;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingPermissionDeleteClickAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.permission.delete.click";
  parameters: EventParameters;

  constructor(params: { projectClassification: ProjectClassification }) {
    this.parameters = {};
    this.parameters.project_classification = params.projectClassification;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export const IssueSettingPermissionLearnMoreClickAnalyticsEvent =
  createAnalyticsEvent("issue.setting.permission.learn_more.click");

export const IssueSettingPermissionLearnMoreExitAnalyticsEvent =
  createAnalyticsEvent("issue.setting.permission.learn_more.exit");

export const IssueSettingPermissionEditDropdownAnalyticsEvent =
  createAnalyticsEvent("issue.setting.permission.edit.dropdown");

export const IssueSettingPermissionFilterOpenAnalyticsEvent =
  createAnalyticsEvent("issue.setting.permission.filter.open");

export class IssueSettingPermissionFilterSuccessAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.permission.filter.success";
  parameters: EventParameters;

  constructor(params: { contributor_type?: any; permissions_level?: any }) {
    this.parameters = {};
    this.parameters.contributor_type = params.contributor_type;
    this.parameters.permissions_level = params.permissions_level;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingCustomFieldCreateAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.custom_field.create";
  parameters: EventParameters;

  constructor(params: {
    customFieldUuid: Uid;
    description?: string;
    title: string;
    fieldType: CustomFieldTypes;
    dropdownOptionCount?: number;
  }) {
    this.parameters = {};
    this.parameters.custom_field_uuid = params.customFieldUuid;
    this.parameters.description = params.description;
    this.parameters.title = params.title;
    this.parameters.field_type = params.fieldType;
    this.parameters.dropdown_option_count = params.dropdownOptionCount;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingCustomFieldEditAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.custom_field.edit";
  parameters: EventParameters;

  constructor(params: {
    customFieldUuid: Uid;
    fieldType: CustomFieldTypes;
    attributeName: EditableCustomFieldAttributes;
    oldAttributeValue: string;
    editedAttributeValue: string;
  }) {
    this.parameters = {};
    this.parameters.custom_field_uuid = params.customFieldUuid;
    this.parameters.field_type = params.fieldType;
    this.parameters.attribute_name = params.attributeName;
    this.parameters.old_attribute_value = params.oldAttributeValue;
    this.parameters.edited_attribute_value = params.editedAttributeValue;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export const IssueSettingCustomFieldDeleteClickAnalyticsEvent =
  createAnalyticsEvent("issue.setting.custom_field.delete.click");

export class IssueSettingCustomFieldDeleteAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.custom_field.delete";
  parameters: EventParameters;

  constructor(params: { customFieldUuid: Uid }) {
    this.parameters = {};
    this.parameters.custom_field_uuid = params.customFieldUuid;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueDrawerViewAnalyticsEvent implements AnalyticsEvent {
  name = "issue.drawer.view";
  parameters: EventParameters;

  constructor(params: { issueUid: Uid; deleted: boolean }) {
    this.parameters = {};
    this.parameters.issue_uid = params.issueUid;
    this.parameters.deleted = params.deleted;
  }
}

export class IssueEditSaveAnalyticsEvent implements AnalyticsEvent {
  name = "issue.edit.save";
  parameters: EventParameters;

  constructor(params: {
    issueUid: Uid;
    field: EditableIssueFields;
    customFieldName?: string;
    watcherCt?: string;
    oldFieldValue?: any;
    editedFieldValue?: any;
    watcherType?: ProjectMemberType[];
    editSource?: UpdateOrigin;
  }) {
    this.parameters = {};
    this.parameters.issue_uid = params.issueUid;
    this.parameters.field = params.field;
    this.parameters.custom_field_name = params.customFieldName;
    this.parameters.watcher_ct = params.watcherCt;
    this.parameters.old_field_value = params.oldFieldValue;
    this.parameters.edited_field_value = params.editedFieldValue;
    this.parameters.watcher_type = params.watcherType;
    this.parameters.edit_source = params.editSource;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueExportConfirmAnalyticsEvent implements AnalyticsEvent {
  name = "issue.export.confirm";
  parameters: EventParameters;

  constructor(params: {
    exportType: IssueExportType;
    fileFormat: IssueExportFileFormat;
    filters?: string[];
    issueCount: number;
    isExportAll: boolean;
  }) {
    this.parameters = {};
    this.parameters.export_type = params.exportType;
    this.parameters.file_format = params.fileFormat;
    this.parameters.filters = params.filters;
    this.parameters.issue_count = params.issueCount;
    this.parameters.is_export_all = params.isExportAll;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueExportShareAnalyticsEvent implements AnalyticsEvent {
  name = "issue.export.share";
  parameters: EventParameters;

  constructor(params: { methodType: IssueExportShareMethodType }) {
    this.parameters = {};
    this.parameters.method_type = params.methodType;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class BcfExportCompletedAnalyticsEvent implements AnalyticsEvent {
  name = "issue.export.completed";
  parameters: EventParameters;

  constructor(params: {
    type: BcfExportType;
    fileFormat: IssueExportFileFormat;
    issueCount: number;
  }) {
    this.parameters = {};
    this.parameters.export_type = params.type;
    this.parameters.file_format = params.fileFormat;
    this.parameters.issue_count = params.issueCount;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueFilterApplyAnalyticsEvent implements AnalyticsEvent {
  name = "issue.filter.apply";
  parameters: EventParameters;

  constructor(params: { field: string; fieldValue: string }) {
    this.parameters = {};
    this.parameters.field = params.field;
    this.parameters.field_value = params.fieldValue;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueFilterClearAllAnalyticsEvent implements AnalyticsEvent {
  name = "issue.filter.clear";
  parameters: EventParameters;

  constructor() {
    this.parameters = {};
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueFilterResetAnalyticsEvent implements AnalyticsEvent {
  name = "issue.filter.reset";
  parameters: EventParameters;

  constructor() {
    this.parameters = {};
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export const IssueRestoreButtonClickAnalyticEvent = createAnalyticsEvent(
  "issue.restore.click"
);

export const IssueRestoreButtonCancelAnalyticEvent = createAnalyticsEvent(
  "issue.restore.cancel"
);

export const IssueRestoreButtonConfirmAnalyticEvent = createAnalyticsEvent(
  "issue.restore.confirm"
);

export class IssueReferenceAddAnalyticsEvent implements AnalyticsEvent {
  name = "issue.reference.add";
  parameters: EventParameters;

  constructor(params: { issueUid: Uid; objectUid: Uid; objectType: string }) {
    this.parameters = {};
    this.parameters.issue_uid = params.issueUid;
    this.parameters.object_uid = params.objectUid;
    this.parameters.object_type = params.objectType;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueReferenceRemoveAnalyticsEvent implements AnalyticsEvent {
  name = "issue.reference.remove";
  parameters: EventParameters;

  constructor(params: { issueUid: Uid; objectUid: Uid; objectType: string }) {
    this.parameters = {};
    this.parameters.issue_uid = params.issueUid;
    this.parameters.object_uid = params.objectUid;
    this.parameters.object_type = params.objectType;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}
export class IssueAttachmentAddAnalyticsEvent implements AnalyticsEvent {
  name = "issue.attachment.add";
  parameters: EventParameters;

  constructor(params: { attachmentFileType: string; issueId: string }) {
    this.parameters = {};
    this.parameters.issue_id = params.issueId;
    this.parameters.attachment_file_type = params.attachmentFileType;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}
export class IssueAttachmentRemoveAnalyticsEvent implements AnalyticsEvent {
  name = "issue.attachment.remove";
  parameters: EventParameters;

  constructor(params: { attachmentFileType: string; issueId: string }) {
    this.parameters = {};
    this.parameters.issue_id = params.issueId;
    this.parameters.attachment_file_type = params.attachmentFileType;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}
export class IssueAttachmentClickAnalyticsEvent implements AnalyticsEvent {
  name = "issue.attachment.click";
  parameters: EventParameters;

  constructor(params: { attachmentFileType: string; issueId: string }) {
    this.parameters = {};
    this.parameters.issue_id = params.issueId;
    this.parameters.attachment_file_type = params.attachmentFileType;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSortApplyAnalyticEvent implements AnalyticsEvent {
  name = "issue.sort.apply";
  parameters: EventParameters;

  constructor(params: { sortBy: string; order: string }) {
    this.parameters = {};
    this.parameters.sort_by = params.sortBy;
    this.parameters.order = params.order;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export const IssueFilterOpenAnalyticsEvent =
  createAnalyticsEvent("issue.filter.open");

export class IssueLogViewAnalyticsEvent implements AnalyticsEvent {
  name = "issue.log.view";
  parameters: EventParameters;

  constructor(params: { isDeletedLog: boolean }) {
    this.parameters = {};
    this.parameters.deleted = params.isDeletedLog;
  }
}

export class IssueLogPaginationAnalyticsEvent implements AnalyticsEvent {
  name = "issue.log.pagination";
  parameters: EventParameters;

  constructor(params: { action: string }) {
    this.parameters = {};
    this.parameters.action = params.action;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueAddCommentsAnalyticsEvent implements AnalyticsEvent {
  name = "issue.add.comments";
  parameters: EventParameters;

  constructor(params: {
    commentCharacters: number;
    numberOfMentions: number;
    numOfUsers: number;
    numOfRoles: number;
    numOfCompanies: number;
    numOfAggregatedMentionees: number;
  }) {
    this.parameters = {};
    this.parameters.issue_add_comments_characters = params.commentCharacters;
    this.parameters.number_of_mentions = params.numberOfMentions;
    this.parameters.number_of_members = params.numOfUsers;
    this.parameters.number_of_roles = params.numOfRoles;
    this.parameters.number_of_companies = params.numOfCompanies;
    this.parameters.aggregated_mentionees = params.numOfAggregatedMentionees;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueThumbnailOpenAnalyticsEvent implements AnalyticsEvent {
  name = "issue.thumbnail.open";
  parameters: EventParameters;

  constructor(params: { placementType: PlacementType }) {
    this.parameters = {};
    this.parameters.placement_document_type = getAnalyticsPlacementType(
      params.placementType
    );
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueThumbnailOpenFromActivityLogAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.activity.thumbnail.open";
  parameters: EventParameters;

  constructor(params: { placementType: PlacementType }) {
    this.parameters = {};
    this.parameters.placement_document_type = getAnalyticsPlacementType(
      params.placementType
    );
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueThumbnailRetakeAnalyticsEvent implements AnalyticsEvent {
  name = "issue.thumbnail.retake";
  parameters: EventParameters;

  constructor(params: { keepOldThumbnail: boolean }) {
    this.parameters = {};
    this.parameters.keep_old_thumbnail = params.keepOldThumbnail;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueThumbnailAnnotateClickAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.thumbnail.annotate.click";
  parameters: EventParameters;

  constructor(params: { issueId: string }) {
    this.parameters = {};
    this.parameters.issue_id = params.issueId;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSnapshotNavigateAwayAnalyticsEvent implements AnalyticsEvent {
  name = "issue.snapshot.navigate_away";
  parameters: EventParameters;

  constructor(params: { placementType: PlacementType }) {
    this.parameters = {};
    this.parameters.placement_document_type = getAnalyticsPlacementType(
      params.placementType
    );
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export enum IssueSnapshotCreateTrigger {
  SAVE_MARKUP_BUTTON = "save_markup_button",
  MOVE_ISSUE = "move_issue",
  CREATE_ISSUE = "create_issue",
  LINK_ISSUE = "link_issue",
  RETAKE_THUMBNAIL_BUTTON = "retake_thumbnail_button",
  EMPTY_STATE_BUTTON = "empty_state_button",
  SET_THUMBNAIL_BUTTON = "set_thumbnail_button",
  NESTED_MARKUP_MODE = "nested_markup_mode",
}

export class IssueSnapshotCreateAnalyticsEvent implements AnalyticsEvent {
  name = "issue.snapshot.create";
  parameters: EventParameters;

  constructor(params: {
    issueId: string;
    trigger: IssueSnapshotCreateTrigger;
    placementType: PlacementType;
  }) {
    this.parameters = {};
    this.parameters.issue_id = params.issueId;
    this.parameters.trigger = params.trigger;
    this.parameters.placement_document_type = getAnalyticsPlacementType(
      params.placementType
    );
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSnapshotStatusAnalyticsEvent implements AnalyticsEvent {
  name = "issue.snapshot.status";
  parameters: EventParameters;

  constructor(params: {
    issueId: string;
    hres: number;
    vres: number;
    hasMarkups: boolean;
    placementType: PlacementType;
  }) {
    this.parameters = {};
    this.parameters.issue_id = params.issueId;
    this.parameters.has_markups = params.hasMarkups;
    this.parameters.hres = params.hres;
    this.parameters.vres = params.vres;
    this.parameters.placement_document_type = getAnalyticsPlacementType(
      params.placementType
    );
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueRemoveIssueAnnotationSuccessAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.remove.issue_annotation.success";
  parameters: EventParameters;

  constructor(params: { issue_uid: string; sheet_uid?: string }) {
    this.parameters = {};
    this.parameters.issue_uid = params.issue_uid;
    this.parameters.sheet_uid = params.sheet_uid;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export const IssueSettingTemplateCreateClickAnalyticsEvent =
  createAnalyticsEvent("issue.setting.template.create.click");

export class IssueSettingTemplateCreateAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.template.create";
  parameters: EventParameters;

  constructor(params: {
    id: string;
    isActive: boolean;
    title: string;
    description?: string;
    status?: IssueStatus;
    rootCauseId?: string;
    locationId?: string;
    locationDetails?: string;
    customAttributes?: IssueCustomAttribute[];
    watchers?: string[];
    assigneeId?: string;
    assignedToType?: string;
    daysTilDueDate?: number;
    daysTilStartDate?: number;
    typeId: string;
    categoryId: string;
    creationOrigin: "normalFlow" | "duplicationFlow";
  }) {
    this.parameters = {};
    this.parameters;
    this.parameters.id = params.id;
    this.parameters.is_active = params.isActive;
    this.parameters.status = params.status;
    this.parameters.root_cause_id = params.rootCauseId;
    this.parameters.location_id = params.locationId;
    this.parameters.watchers = params.watchers;
    this.parameters.assignee_id = params.assigneeId;
    this.parameters.assigned_to_type = params.assignedToType;
    this.parameters.days_til_due_date = params.daysTilDueDate;
    this.parameters.days_til_start_date = params.daysTilStartDate;
    this.parameters.type_id = params.typeId;
    this.parameters.category_id = params.categoryId;
    this.parameters.custom_attributes = params.customAttributes;
    this.parameters.creation_origin = params.creationOrigin;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingTemplateCreateCancelAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.setting.template.create.cancel";
  parameters: EventParameters;

  constructor(params: { creationOrigin: "normalFlow" | "duplicationFlow" }) {
    this.parameters = {};
    this.parameters;

    this.parameters.creation_origin = params.creationOrigin;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueSettingTemplateEditAnalyticsEvent implements AnalyticsEvent {
  name = "issue.setting.template.edit";
  parameters: EventParameters;

  constructor(params: { editedAttribute: IssueTemplateChange }) {
    this.parameters = {};
    this.parameters;
    this.parameters.edited_attribute = params.editedAttribute;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export const IssueSettingTemplateDeleteClickAnalyticsEvent =
  createAnalyticsEvent("issue.setting.template.delete.click");

export class IssueSettingTemplateDeleteAnalyticsEvent
  implements AnalyticsEvent
{
  name: string;
  parameters: EventParameters;

  constructor(params: { eventName: string; templateId: Uid }) {
    this.name = params.eventName;
    this.parameters = {};
    this.parameters;
    this.parameters.template_id = params.templateId;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export const IssueSettingTemplateDuplicateClickAnalyticsEvent =
  createAnalyticsEvent("issue.setting.template.duplicate.click");

export type ReferencePickerContextType =
  | "photo"
  | "form"
  | "asset"
  | "sheet"
  | "doc"
  | "cost"
  | "submittal"
  | "meeting"
  | "schedule"
  | "rfi"
  | "unknown"
  | "none";

export type IssueStatus =
  | "draft"
  | "open"
  | "pending"
  | "in_progress"
  | "completed"
  | "in_review"
  | "not_approved"
  | "in_dispute"
  | "closed";

export type ActiveStatus = "active" | "inactive";

export type IssueConfigurationPageType =
  | "types"
  | "custom_fields"
  | "permissions"
  | "root_causes";

export type PermissionEntityTypes = "user" | "role" | "company";

export type PermissionsLevels =
  | "tier_1"
  | "tier_1_1"
  | "tier_2"
  | "tier_3"
  | "tier_4"
  | "other";

export type CustomFieldTypes =
  | "dropdown"
  | "numeric"
  | "paragraph"
  | "text"
  | "date"
  | "list";

export type IssueExportType = "detail" | "summary";

export type BcfExportType = "Single" | "Bulk";

export type IssueExportFileFormat = "pdf" | "xlsx" | "BCF";

export type IssueExportShareMethodType = "copy_link" | "download";

export type EditableIssueFields =
  | "is_published"
  | "title"
  | "status"
  | "type"
  | "category"
  | "description"
  | "assignee"
  | "start_date"
  | "due_date"
  | "location"
  | "location_details"
  | "root_cause"
  | "watcher_added"
  | "watcher_removed"
  | "cf_dropdown_type"
  | "cf_text_type"
  | "cf_numeric_type"
  | "cf_paragraph_type"
  | "cf_date_type"
  | "cf_list_type";

export type BaseIssueAttributeToAnalyticsFieldKey = keyof Partial<Issue> &
  "placement";

/** watchers aren't included in this object because it has two properties (watcher_added, watcher_removed) that can be tracked */
export const BASE_ISSUE_ATTRIBUTE_TO_ANALYTICS_FIELD: {
  [key in BaseIssueAttributeToAnalyticsFieldKey]: EditableIssueFields;
} = {
  title: "title",
  description: "description",
  status: "status",
  assignedTo: "assignee",
  startDate: "start_date",
  dueDate: "due_date",
  locationDetails: "location_details",
  locationId: "location",
  issueTypeId: "type",
  rootCauseId: "root_cause",
  published: "is_published",
  placement: "placement",
};

export class IssueStatusSettingActivateAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.status.activate";
  parameters: EventParameters;

  constructor(params: { statusName: string }) {
    this.parameters = {};
    this.parameters.status = params.statusName;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class IssueStatusSettingDeactivateAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.status.deactivate";
  parameters: EventParameters;

  constructor(params: { statusName: string }) {
    this.parameters = {};
    this.parameters.status = params.statusName;
    this.parameters["analyticsLib"] = "0.0.93";
  }
}

export class QRPReportConfirmEvent implements AnalyticsEvent {
  name = "issue.export.confirm";
  parameters: EventParameters;

  constructor(params: { isIssueDeleted: boolean }) {
    this.parameters = {};
    this.parameters.deleted = params.isIssueDeleted;
  }
}

export class QRPReportCompletedEvent implements AnalyticsEvent {
  name = "issue.export.completed";
  parameters: EventParameters;

  constructor(params: { isIssueDeleted: boolean }) {
    this.parameters = {};
    this.parameters.deleted = params.isIssueDeleted;
  }
}

export class IssueWatcherSelect implements AnalyticsEvent {
  name = "issue.watcher.select";
  parameters: EventParameters;

  constructor(params: { issueId: string; watcherType: ProjectMemberType }) {
    this.parameters = {};
    this.parameters.issue_id = params.issueId;
    this.parameters.watcher_type = params.watcherType;
  }
}

export class IssueWatcherDeselect implements AnalyticsEvent {
  name = "issue.watcher.deselect";
  parameters: EventParameters;

  constructor(params: { issueId: string; watcherType: ProjectMemberType }) {
    this.parameters = {};
    this.parameters.issue_id = params.issueId;
    this.parameters.watcher_type = params.watcherType;
  }
}

export class PlacementLinkClick implements AnalyticsEvent {
  name = "issue.placement.click";
  parameters: EventParameters;

  constructor(params: { issueId: string }) {
    this.parameters = {};
    this.parameters.issue_id = params.issueId;
  }
}

export class PlacementDeepLinkClick implements AnalyticsEvent {
  name = "issue.placement.deeplink";
  parameters: EventParameters;

  constructor(params: { issueId: string; product?: string; tool?: string }) {
    this.parameters = {};
    this.parameters.issue_id = params.issueId;
    this.parameters.product = params.product;
    this.parameters.tool = params.tool;
  }
}

export class PlacementFallbackClick implements AnalyticsEvent {
  name = "issue.placement.fallback";
  parameters: EventParameters;

  constructor(params: { issueId: string; product?: string; tool?: string }) {
    this.parameters = {};
    this.parameters.issue_id = params.issueId;
    this.parameters.product = params.product;
    this.parameters.tool = params.tool;
  }
}

export enum AccordionActionType {
  OPEN = "open",
  CLOSE = "close",
}

export class EmptyFieldsAccordionActionAnalyticsEvent
  implements AnalyticsEvent
{
  name = "issue.empty_fields.click";
  parameters: EventParameters;

  constructor(params: { action: AccordionActionType }) {
    this.parameters = {};
    this.parameters.action = params.action;
  }
}

export type EditableCustomFieldAttributes = "title" | "description" | "Options";

export type ReferenceType =
  | "photo"
  | "files"
  | "snapshot"
  | "issue"
  | "form"
  | "asset"
  | "sheet"
  | "meeting"
  | "rfi";
